/**
 * Enhanced Deduplication Manager for RequestCoordinator Integration
 *
 * This module provides comprehensive request deduplication capabilities integrated
 * with the RequestCoordinator architecture. It prevents duplicate requests while
 * being aware of retry context, circuit breaker states, and providing detailed
 * analytics and notifications.
 *
 * Key Features:
 * - Retry-aware deduplication (doesn't dedupe across retry attempts)
 * - Circuit breaker integration
 * - Real-time analytics and notifications
 * - Configurable cache management
 * - Memory leak prevention
 * - Performance optimized
 */
import { getObservability } from '../../../di/container';
import { getSessionEpoch } from '../../../store/session-scope';
import {
  type DeduplicationKey,
  type DeduplicationEntry,
  type DeduplicationOptions,
  type DeduplicationStats,
  type DeduplicationResult,
  type RequestContext,
  type RequestConfig,
  type CoordinatorNotificationEvent,
  type IDeduplicationManager,
  NotificationType as CoordinatorNotificationType,
  DEFAULT_DEDUPLICATION_OPTIONS,
} from '../../../types/api/request.types';
import { dayjs } from '../../../utils/date';

// =============================================================================
// KEY GENERATION UTILITIES
// =============================================================================

/**
 * FNV-1a string hash (32-bit).
 *
 * Folds every character of the input into the result, unlike a truncated
 * base64 encoding which only preserves a prefix of the input. This matters for
 * the cache key: the request body is appended last, so prefix-only hashing
 * would collide late-diverging bodies (e.g. concurrent same-endpoint POSTs that
 * share an opening `{"name":...`), silently deduplicating distinct requests.
 *
 * Dependency-free and synchronous so it runs identically on web and React
 * Native (no crypto.subtle / Node crypto availability concerns).
 */
function fnv1aHash(str: string): string {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime, with 32-bit overflow
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Generate a cache key from deduplication parameters
 */
function generateCacheKey(
  key: DeduplicationKey,
  options: DeduplicationOptions
): string {
  const parts: string[] = [key.method.toUpperCase(), normalizeUrl(key.url)];

  // Include query parameters
  if (key.params) {
    const sortedParams = sortObject(key.params);
    parts.push(`params:${JSON.stringify(sortedParams)}`);
  }

  // Include relevant headers if enabled
  if (options.includeHeaders && key.headers) {
    const relevantHeaders = filterRelevantHeaders(key.headers);
    if (Object.keys(relevantHeaders).length > 0) {
      parts.push(`headers:${JSON.stringify(sortObject(relevantHeaders))}`);
    }
  }

  // Include request body if enabled
  if (options.includeBody && key.body) {
    const normalizedBody = normalizeData(key.body);
    parts.push(`body:${JSON.stringify(normalizedBody)}`);
  }

  // Include retry context if enabled and available
  if (options.includeRetryContext && key.retryContext) {
    parts.push(
      `retry:${key.retryContext.attempt}:${key.retryContext.parentRequestId}`
    );
  }

  // Include circuit breaker state if available
  if (key.circuitBreakerState) {
    parts.push(`cb:${key.circuitBreakerState}`);
  }

  // The session that is asking. Without it, an identical GET from the next
  // account would join the previous account's request still in flight and
  // receive its response.
  parts.push(`session:${getSessionEpoch()}`);

  // Create hash of the combined key
  const combinedKey = parts.join('|');
  return fnv1aHash(combinedKey);
}

/**
 * Normalize URL by removing protocol, domain, and fragment
 */
function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url, 'http://localhost');
    return `${urlObj.pathname}${urlObj.search}`;
  } catch {
    const withoutQuery = url.split('?')[0] ?? url;
    return withoutQuery.split('#')[0] ?? withoutQuery;
  }
}

/**
 * Sort object keys recursively for consistent serialization
 */
function sortObject(obj: Record<string, unknown>): Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return obj;

  const sorted: Record<string, unknown> = {};
  Object.keys(obj)
    .sort()
    .forEach((key) => {
      const value = obj[key];
      sorted[key] =
        value && typeof value === 'object' && !Array.isArray(value)
          ? sortObject(value as Record<string, unknown>)
          : value;
    });

  return sorted;
}

/**
 * A body whose contents can't be read into a key: multipart and binary
 * payloads. Every FormData serialises to the same `{}`, so two different
 * uploads to one URL would share a key and the second would be handed the
 * first's response. These requests are never merged.
 */
function isOpaqueBody(body: unknown): boolean {
  if (body === null || body === undefined) return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  return body instanceof ArrayBuffer || ArrayBuffer.isView(body);
}

/**
 * Normalize data for consistent serialization
 */
function normalizeData(data: unknown): unknown {
  if (data === null || data === undefined) return null;
  if (
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  ) {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(normalizeData);
  }
  if (typeof data === 'object') {
    return sortObject(data as Record<string, unknown>);
  }
  return String(data);
}

/**
 * Filter headers to only include relevant ones for caching
 */
function filterRelevantHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const relevantHeaders: Record<string, string> = {};
  const includeHeaders = [
    'authorization',
    'content-type',
    'accept',
    'accept-language',
    'cache-control',
    'x-api-key',
    'x-client-version',
  ];

  includeHeaders.forEach((header) => {
    const value = headers[header] || headers[header.toLowerCase()];
    if (value) {
      relevantHeaders[header.toLowerCase()] = value;
    }
  });

  return relevantHeaders;
}

// =============================================================================
// ENHANCED DEDUPLICATION MANAGER IMPLEMENTATION
// =============================================================================

export class EnhancedDeduplicationManager implements IDeduplicationManager {
  private cache: Map<string, DeduplicationEntry> = new Map();
  private stats: DeduplicationStats = {
    totalRequests: 0,
    deduplicatedRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    cacheSize: 0,
    duplicateSavings: 0,
    averageResponseTime: 0,
  };
  private options: Required<DeduplicationOptions>;
  private cleanupIntervalId?: ReturnType<typeof setInterval>;
  private responseTimes: number[] = [];
  private notificationHandlers: Set<
    (event: CoordinatorNotificationEvent) => void
  > = new Set();
  private observability = getObservability();

  constructor(options: DeduplicationOptions = {}) {
    this.options = {
      ...DEFAULT_DEDUPLICATION_OPTIONS,
      ...options,
    } as Required<DeduplicationOptions>;

    // Set default key generator if not provided
    if (!this.options.keyGenerator) {
      this.options.keyGenerator = (key): string =>
        generateCacheKey(key, this.options);
    }

    this.startCleanup();
    this.logDebug('Deduplication manager initialized', this.options);
  }

  // =========================================================================
  // PUBLIC API METHODS
  // =========================================================================

  async execute<T>(
    key: DeduplicationKey,
    requestFn: () => Promise<T>
  ): Promise<DeduplicationResult<T>> {
    if (!this.options.enabled || isOpaqueBody(key.body)) {
      const startTime = dayjs().valueOf();
      const result = await requestFn();
      return {
        data: result,
        wasDeduplicated: false,
        cacheKey: '',
        subscribers: 0,
        responseTime: dayjs().diff(startTime),
      };
    }

    this.stats.totalRequests++;
    const startTime = dayjs().valueOf();
    const cacheKey = this.options.keyGenerator!(key);

    // Check for existing entry
    const existingEntry = this.cache.get(cacheKey);

    if (existingEntry && this.isEntryValid(existingEntry)) {
      return this.handleCacheHit<T>(existingEntry, cacheKey, startTime);
    }

    // Create new entry for cache miss
    return this.handleCacheMiss<T>(key, cacheKey, requestFn, startTime);
  }

  isPending(key: DeduplicationKey): boolean {
    if (!this.options.enabled) return false;

    const cacheKey = this.options.keyGenerator!(key);
    const entry = this.cache.get(cacheKey);
    return entry ? this.isEntryValid(entry) : false;
  }

  cancel(key: DeduplicationKey): boolean {
    if (!this.options.enabled) return false;

    const cacheKey = this.options.keyGenerator!(key);
    const entry = this.cache.get(cacheKey);

    if (!entry) return false;

    // Reject all subscribers
    const error = new Error('Request cancelled');
    entry.subscribers.forEach((subscriber) => {
      try {
        subscriber.reject(error);
      } catch (err) {
        this.logError('Error rejecting subscriber', err);
      }
    });

    // Remove from cache
    this.cache.delete(cacheKey);
    this.updateCacheSize();

    this.notifyEvent({
      type: CoordinatorNotificationType.REQUEST_CANCELLED,
      requestId: entry.requestId,
      message: `Cancelled deduplicated request for ${entry.metadata.method} ${entry.metadata.url}`,
      timestamp: dayjs().valueOf(),
      metadata: { cacheKey, subscribers: entry.subscribers.size },
    });

    return true;
  }

  subscribe<T>(key: DeduplicationKey): Promise<T> | null {
    if (!this.options.enabled) return null;

    const cacheKey = this.options.keyGenerator!(key);
    const entry = this.cache.get(cacheKey);

    if (!entry || !this.isEntryValid(entry)) return null;

    // Create new promise for this subscriber
    return new Promise<T>((resolve, reject) => {
      const subscriber = {
        resolve: resolve as (value: unknown) => void,
        reject,
        requestId: this.generateRequestId(),
      };

      entry.subscribers.add(subscriber);
      this.logDebug('Added subscriber to existing request', {
        cacheKey: cacheKey.substring(0, 16),
        totalSubscribers: entry.subscribers.size,
      });
    });
  }

  clear(): void {
    const entriesCount = this.cache.size;

    // Reject all pending requests
    this.cache.forEach((entry) => {
      const error = new Error('Cache cleared');
      entry.subscribers.forEach((subscriber) => {
        try {
          subscriber.reject(error);
        } catch (err) {
          this.logError('Error rejecting subscriber during clear', err);
        }
      });
    });

    this.cache.clear();
    this.stats.cacheSize = 0;

    this.logDebug('Cache cleared', { entriesCount });

    this.notifyEvent({
      type: CoordinatorNotificationType.DEDUPLICATION_CACHE_MISS,
      requestId: 'system',
      message: `Cleared ${entriesCount} cached requests`,
      timestamp: dayjs().valueOf(),
      metadata: { entriesCount },
    });
  }

  getStats(): DeduplicationStats {
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      averageResponseTime: this.calculateAverageResponseTime(),
    };
  }

  /**
   * Report aggregate deduplication metrics to observability
   * Only called on destroy() to minimize overhead
   */
  private reportMetrics(): void {
    const stats = this.getStats();

    // Total requests
    this.observability.recordMetric(
      'deduplication.total_requests',
      stats.totalRequests,
      'count'
    );

    // Calculate and report cache hit rate
    const hitRate =
      stats.totalRequests > 0
        ? (stats.cacheHits / stats.totalRequests) * 100
        : 0;

    this.observability.recordMetric(
      'deduplication.cache_hit_rate',
      hitRate,
      'percentage'
    );

    // Duplicate savings
    this.observability.recordMetric(
      'deduplication.duplicate_savings',
      stats.duplicateSavings,
      'count'
    );

    // Final cache size (only reported here to minimize overhead)
    this.observability.recordMetric(
      'deduplication.cache_size',
      stats.cacheSize,
      'count'
    );
  }

  updateConfig(options: Partial<DeduplicationOptions>): void {
    const oldOptions = { ...this.options };
    this.options = {
      ...this.options,
      ...options,
    } as Required<DeduplicationOptions>;

    // Restart cleanup if interval changed
    if (oldOptions.ttl !== this.options.ttl) {
      this.stopCleanup();
      this.startCleanup();
    }

    this.logDebug('Configuration updated', {
      old: oldOptions,
      new: this.options,
    });
  }

  cleanup(): number {
    const now = dayjs().valueOf();
    let cleanedCount = 0;
    const keysToDelete: string[] = [];

    // Identify expired entries
    this.cache.forEach((entry, key) => {
      if (now > entry.expiresAt) {
        keysToDelete.push(key);

        // Reject any remaining subscribers with timeout error
        const error = new Error('Request timed out');
        entry.subscribers.forEach((subscriber) => {
          try {
            subscriber.reject(error);
          } catch (err) {
            this.logError('Error rejecting expired subscriber', err);
          }
        });
      }
    });

    // Remove expired entries
    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      cleanedCount++;
    });

    if (cleanedCount > 0) {
      this.updateCacheSize();
      this.logDebug('Cleanup completed', {
        cleanedCount,
        remainingEntries: this.cache.size,
      });
    }

    return cleanedCount;
  }

  destroy(): void {
    // Report final metrics before cleanup
    this.reportMetrics();

    this.stopCleanup();
    this.clear();
    this.notificationHandlers.clear();
    this.responseTimes = [];
    this.logDebug('Deduplication manager destroyed');
  }

  // =========================================================================
  // NOTIFICATION SYSTEM INTEGRATION
  // =========================================================================

  addNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): () => void {
    this.notificationHandlers.add(handler);
    return () => this.notificationHandlers.delete(handler);
  }

  // =========================================================================
  // PRIVATE IMPLEMENTATION METHODS
  // =========================================================================

  private async handleCacheHit<T>(
    entry: DeduplicationEntry,
    cacheKey: string,
    startTime: number
  ): Promise<DeduplicationResult<T>> {
    this.stats.cacheHits++;
    this.stats.deduplicatedRequests++;

    const responseTime = dayjs().valueOf() - startTime;
    this.recordResponseTime(responseTime);

    // Track cache hit metric (lightweight counter)
    this.observability.recordMetric('deduplication.cache_hit', 1, 'count', {
      endpoint: entry.metadata.url || 'unknown',
    });

    this.logDebug('Cache hit', {
      cacheKey: cacheKey.substring(0, 16),
      method: entry.metadata.method,
      url: entry.metadata.url,
      subscribers: entry.subscribers.size,
    });

    this.notifyEvent({
      type: CoordinatorNotificationType.DEDUPLICATION_CACHE_HIT,
      requestId: entry.requestId,
      message: `Cache hit for ${entry.metadata.method} ${entry.metadata.url}`,
      timestamp: dayjs().valueOf(),
      metadata: {
        cacheKey,
        subscribers: entry.subscribers.size,
        responseTime,
        retryAttempt: entry.metadata.retryAttempt,
      },
    });

    const result = (await entry.promise) as T;
    return {
      data: result,
      wasDeduplicated: true,
      cacheKey,
      subscribers: entry.subscribers.size,
      responseTime,
    };
  }

  private async handleCacheMiss<T>(
    key: DeduplicationKey,
    cacheKey: string,
    requestFn: () => Promise<T>,
    startTime: number
  ): Promise<DeduplicationResult<T>> {
    this.stats.cacheMisses++;

    // Track cache miss metric
    this.observability.recordMetric('deduplication.cache_miss', 1, 'count', {
      endpoint: key.url || 'unknown',
    });

    const requestId = this.generateRequestId();
    const subscribers = new Set<{
      resolve: (value: unknown) => void;
      reject: (error: unknown) => void;
      requestId: string;
    }>();

    // Create the shared promise
    const sharedPromise = new Promise<T>((resolve, reject) => {
      const requestStartTime = dayjs();
      requestFn()
        .then((result) => {
          // Track request duration
          const duration = dayjs().diff(requestStartTime);
          this.observability.recordMetric(
            'deduplication.request_duration',
            duration,
            'milliseconds',
            { endpoint: key.url || 'unknown', cached: 'false' }
          );

          // Notify all subscribers of success
          subscribers.forEach((subscriber) => {
            try {
              subscriber.resolve(result);
            } catch (error) {
              this.logError('Error resolving subscriber', error);
            }
          });

          resolve(result);

          // Deduplicate only while a request is genuinely in-flight: drop the
          // entry the instant it resolves. Deduplication collapses CONCURRENT
          // duplicates (e.g. StrictMode double-mount, two components mounting at
          // once) into a single network call — it is NOT a response cache. A
          // trailing post-completion window would (a) serve a stale/incorrect
          // response to a sequential identical request issued shortly after,
          // and (b) silently merge two deliberate identical actions. Keeping it
          // strictly in-flight is correct for both reads and mutations.
          this.cache.delete(cacheKey);
          this.updateCacheSize();
        })
        .catch((error) => {
          // Notify all subscribers of error
          subscribers.forEach((subscriber) => {
            try {
              subscriber.reject(error);
            } catch (err) {
              this.logError('Error rejecting subscriber', err);
            }
          });

          reject(error);

          // Remove failed request from cache immediately
          this.cache.delete(cacheKey);
          this.updateCacheSize();
        });
    });

    // Create cache entry
    const entry: DeduplicationEntry = {
      key: cacheKey,
      promise: sharedPromise,
      requestId,
      createdAt: dayjs().valueOf(),
      expiresAt: dayjs().add(this.options.ttl, 'millisecond').valueOf(),
      subscribers,
      metadata: {
        method: key.method,
        url: key.url,
        retryAttempt: key.retryContext?.attempt,
        circuitBreakerState: key.circuitBreakerState,
      },
    };

    // Store in cache
    this.cache.set(cacheKey, entry);
    this.enforceMaxCacheSize();
    this.updateCacheSize();

    const responseTime = dayjs().valueOf() - startTime;
    this.recordResponseTime(responseTime);

    this.logDebug('Cache miss - executing request', {
      cacheKey: cacheKey.substring(0, 16),
      method: key.method,
      url: key.url,
      retryAttempt: key.retryContext?.attempt,
    });

    this.notifyEvent({
      type: CoordinatorNotificationType.DEDUPLICATION_CACHE_MISS,
      requestId,
      message: `Cache miss for ${key.method} ${key.url}`,
      timestamp: dayjs().valueOf(),
      metadata: {
        cacheKey,
        method: key.method,
        url: key.url,
        retryAttempt: key.retryContext?.attempt,
        circuitBreakerState: key.circuitBreakerState,
      },
    });

    const result = await sharedPromise;
    return {
      data: result,
      wasDeduplicated: false,
      cacheKey,
      subscribers: subscribers.size,
      responseTime,
    };
  }

  private isEntryValid(entry: DeduplicationEntry): boolean {
    return dayjs().valueOf() < entry.expiresAt;
  }

  private enforceMaxCacheSize(): void {
    if (this.cache.size <= this.options.maxCacheSize) return;

    // Remove oldest entries
    const entriesToRemove = this.cache.size - this.options.maxCacheSize;
    const sortedEntries = Array.from(this.cache.entries()).sort(
      (a, b) => a[1].createdAt - b[1].createdAt
    );

    for (let i = 0; i < entriesToRemove; i++) {
      const pair = sortedEntries[i];
      if (!pair) continue;
      const [key, entry] = pair;

      // Reject subscribers with cache eviction error
      const error = new Error('Cache evicted due to size limit');
      entry.subscribers.forEach(
        (subscriber: { reject: (error: unknown) => void }) => {
          try {
            subscriber.reject(error);
          } catch (err) {
            this.logError('Error rejecting evicted subscriber', err);
          }
        }
      );

      this.cache.delete(key);
    }

    this.logDebug('Enforced max cache size', {
      removed: entriesToRemove,
      currentSize: this.cache.size,
    });
  }

  private updateCacheSize(): void {
    this.stats.cacheSize = this.cache.size;
  }

  private calculateAverageResponseTime(): number {
    if (this.responseTimes.length === 0) return 0;
    const sum = this.responseTimes.reduce((acc, time) => acc + time, 0);
    return sum / this.responseTimes.length;
  }

  private recordResponseTime(time: number): void {
    this.responseTimes.push(time);

    // Keep only recent response times (last 100)
    if (this.responseTimes.length > 100) {
      this.responseTimes = this.responseTimes.slice(-100);
    }
  }

  private startCleanup(): void {
    if (this.cleanupIntervalId) return;

    const cleanupInterval = Math.max(this.options.ttl / 4, 5000); // At least every 5 seconds
    this.cleanupIntervalId = setInterval(() => {
      this.cleanup();
    }, cleanupInterval);
    // Don't let the cleanup timer keep a Node/test event loop alive (no-op on RN/browser).
    (this.cleanupIntervalId as unknown as { unref?: () => void }).unref?.();
  }

  private stopCleanup(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = undefined;
    }
  }

  private generateRequestId(): string {
    return `dedup_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private notifyEvent(event: CoordinatorNotificationEvent): void {
    this.notificationHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        this.logError('Notification handler error', error);
      }
    });
  }

  private logDebug(message: string, data?: unknown): void {
    if (this.options.enableLogging) {
      console.log(`[DeduplicationManager] ${message}`, data || '');
    }
  }

  private logError(message: string, error: unknown): void {
    if (this.options.enableLogging) {
      console.error(`[DeduplicationManager] ${message}`, error);
    }
  }
}

// =============================================================================
// FACTORY FUNCTIONS AND UTILITIES
// =============================================================================

/**
 * Create a new deduplication manager instance
 */
export function createDeduplicationManager(
  options: DeduplicationOptions = {}
): IDeduplicationManager {
  return new EnhancedDeduplicationManager(options);
}

/**
 * Create deduplication key from request context
 */
export function createDeduplicationKeyFromContext(
  context: RequestContext
): DeduplicationKey {
  return {
    method: context.config.method || 'GET',
    url: context.config.url || '',
    params: context.config.params,
    headers: context.config.headers as Record<string, string>,
    body: context.config.data,
    retryContext:
      context.retryCount > 0
        ? {
            attempt: context.retryCount,
            parentRequestId: context.metadata.parentRequestId || context.id,
          }
        : undefined,
    circuitBreakerState: context.metadata.endpoint ? undefined : undefined, // Will be populated by circuit breaker
  };
}

/**
 * Create deduplication key from request config
 */
export function createDeduplicationKeyFromConfig(
  config: RequestConfig
): DeduplicationKey {
  return {
    method: config.method || 'GET',
    url: config.url || '',
    params: config.params,
    headers: config.headers as Record<string, string>,
    body: config.data,
    retryContext: config.metadata?.retryCount
      ? {
          attempt: config.metadata.retryCount,
          parentRequestId:
            config.metadata.parentRequestId || config.metadata.requestId,
        }
      : undefined,
  };
}

/**
 * Default deduplication manager instance
 */
export const defaultDeduplicationManager = createDeduplicationManager();

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Execute request with default deduplication manager
 */
export async function deduplicateRequest<T>(
  key: DeduplicationKey,
  requestFn: () => Promise<T>
): Promise<DeduplicationResult<T>> {
  return defaultDeduplicationManager.execute(key, requestFn);
}

/**
 * Check if request is pending with default manager
 */
export function isRequestPending(key: DeduplicationKey): boolean {
  return defaultDeduplicationManager.isPending(key);
}

/**
 * Subscribe to pending request with default manager
 */
export function subscribeToRequest<T>(
  key: DeduplicationKey
): Promise<T> | null {
  return defaultDeduplicationManager.subscribe(key);
}

/**
 * Get deduplication statistics from default manager
 */
export function getDeduplicationStats(): DeduplicationStats {
  return defaultDeduplicationManager.getStats();
}
